// Right sidebar — the drafts list. New draft, switch drafts, delete,
// and the private-repo backup indicator (drafts persist locally
// instantly; this shows the debounced push to GitHub).

import { useComposeStore } from "../../store/composeStore";

function backupLabel(s: string): string {
  switch (s) {
    case "saving": return "backing up…";
    case "saved": return "backed up ✓";
    case "error": return "backup failed";
    default: return "local only";
  }
}

export function DraftsPane() {
  const drafts = useComposeStore((s) => s.drafts);
  const activeId = useComposeStore((s) => s.activeId);
  const backup = useComposeStore((s) => s.backup);
  const newDraft = useComposeStore((s) => s.newDraft);
  const selectDraft = useComposeStore((s) => s.selectDraft);
  const removeDraft = useComposeStore((s) => s.removeDraft);
  const backupNow = useComposeStore((s) => s.backupNow);

  return (
    <div className="dr-pane">
      <div className="dr-head">
        <span className="dr-head-title">Drafts</span>
        <button className="dr-new" onClick={() => void newDraft()}>+ new</button>
      </div>

      <div className="dr-list">
        {drafts.map((d) => (
          <div
            key={d.id}
            className={"dr-item" + (d.id === activeId ? " dr-item-active" : "")}
            onClick={() => void selectDraft(d.id)}
          >
            <div className="dr-item-main">
              <div className="dr-item-title">{d.title.trim() || "Untitled"}</div>
              <div className="dr-item-sub">
                {d.published_slug ? "published" : "draft"} · {new Date(d.updated_at).toISOString().slice(5, 10)}
              </div>
            </div>
            {drafts.length > 1 && (
              <button
                className="dr-del"
                title="delete draft"
                onClick={(e) => { e.stopPropagation(); if (confirm("Delete this draft?")) void removeDraft(d.id); }}
              >×</button>
            )}
          </div>
        ))}
      </div>

      <div className="dr-foot">
        <span className={"dr-backup dr-backup-" + backup}>{backupLabel(backup)}</span>
        <button className="dr-backup-now" onClick={() => void backupNow()}>back up now</button>
      </div>

      <style>{`
        .dr-pane { display: flex; flex-direction: column; height: 100%; min-height: 0; background: var(--paper-raised); }
        .dr-head { flex: none; display: flex; align-items: center; justify-content: space-between; padding: var(--space-4); border-bottom: 1px solid var(--line); }
        .dr-head-title { font-family: var(--font-serif); font-size: 16px; font-weight: 600; color: var(--ink); }
        .dr-new { font-family: var(--font-sans); font-size: 12px; font-weight: 600; color: var(--teal); background: var(--teal-dim); border: none; border-radius: 999px; padding: 4px 12px; cursor: pointer; }
        .dr-list { flex: 1; overflow: auto; padding: var(--space-2); display: flex; flex-direction: column; gap: 2px; }
        .dr-item { display: flex; align-items: center; gap: var(--space-2); padding: var(--space-3); border-radius: var(--radius-1); cursor: pointer; }
        .dr-item:hover { background: var(--paper-sunk); }
        .dr-item-active, .dr-item-active:hover { background: var(--teal-dim); }
        .dr-item-main { flex: 1; min-width: 0; }
        .dr-item-title { font-size: 14px; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .dr-item-sub { font-family: var(--font-mono); font-size: 10px; color: var(--ink-faint); margin-top: 2px; }
        .dr-del { flex: none; width: 22px; height: 22px; border: none; background: transparent; color: var(--ink-faint); font-size: 18px; line-height: 1; border-radius: 6px; cursor: pointer; }
        .dr-del:hover { background: var(--rust-bg); color: var(--rust); }
        .dr-foot { flex: none; display: flex; align-items: center; justify-content: space-between; padding: var(--space-3) var(--space-4); border-top: 1px solid var(--line); }
        .dr-backup { font-family: var(--font-mono); font-size: 10px; color: var(--ink-faint); }
        .dr-backup-saved { color: var(--teal); }
        .dr-backup-error { color: var(--rust); }
        .dr-backup-now { font-family: var(--font-mono); font-size: 10px; color: var(--ink-soft); background: none; border: none; text-decoration: underline; cursor: pointer; }
        .dr-backup-now:hover { color: var(--teal); }
      `}</style>
    </div>
  );
}
